// Use the external domain for API calls
const API_BASE_URL = window.location.origin + '/api';

class ApiService {
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Get the access token from localStorage
    const accessToken = localStorage.getItem('accessToken');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        ...options.headers,
      },
      ...options,
    };

try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message =
          errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
        const error = new Error(message);

        // Callers throughout the app read `err.response.data.error` (an axios
        // convention). This client uses fetch, so that path was always
        // undefined and every caller silently fell through to its generic
        // fallback message. Attaching an axios-shaped `response` makes all
        // existing call sites work without editing them. Finding F-025.
        error.status = response.status;
        error.response = { status: response.status, data: errorData };
        // Validation endpoints return an `errors` array alongside `error`.
        error.errors = errorData.errors || (errorData.error ? [errorData.error] : []);

        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Generic HTTP methods
  async get(endpoint) {
    return { data: await this.request(endpoint, { method: 'GET' }) };
  }

  async post(endpoint, data) {
    return { data: await this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }) };
  }

  async put(endpoint, data) {
    return { data: await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }) };
  }

async delete(endpoint, body) {
    return { data: await this.request(endpoint, { method: 'DELETE', ...(body ? { body: JSON.stringify(body) } : {}) }) };
  }

  // Workspace-specific methods
async getWorkspaces(agencyId) {
    const url = agencyId ? `/workspaces?agencyId=${agencyId}` : '/workspaces';
    return this.request(url);
  }

  async getWorkspace(id) {
    return this.request(`/workspaces/${id}`);
  }

  async createWorkspace(data) {
    return this.request('/workspaces', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deployWorkspace(id) {
    return this.request(`/workspaces/${id}/deploy`, {
      method: 'POST',
    });
  }

  // Health check
  async getHealth() {
    return this.request('/health');
  }

  // ==================== BILLING ====================
  
  // Billing Profiles
  async getBillingProfiles() {
    return this.request('/billing/profiles');
  }
  
  async createBillingProfile(data) {
    return this.request('/billing/profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  
  async updateBillingProfile(id, data) {
    return this.request(`/billing/profiles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  
  async setDefaultBillingProfile(id) {
    return this.request(`/billing/profiles/${id}/default`, {
      method: 'PUT',
    });
  }
  
  async deleteBillingProfile(id) {
    return this.request(`/billing/profiles/${id}`, {
      method: 'DELETE',
    });
  }
  
  // Payment Methods
  async getPaymentMethods(profileId) {
    return this.request(`/billing/profiles/${profileId}/payment-methods`);
  }
  
  async createPaymentIntent(profileId) {
    return this.request(`/billing/profiles/${profileId}/payment-intent`, {
      method: 'POST',
    });
  }
  
  async addStripePaymentMethod(profileId, paymentMethodId, gatewayId, billingAddress) {
    return this.request(`/billing/profiles/${profileId}/payment-methods/stripe`, {
      method: 'POST',
      body: JSON.stringify({ paymentMethodId, gatewayId, billingAddress }),
    });
  }
  
  async addAuthnetPaymentMethod(profileId, opaqueData, gatewayId) {
    return this.request(`/billing/profiles/${profileId}/payment-methods/authnet`, {
      method: 'POST',
      body: JSON.stringify({ opaqueData, gatewayId }),
    });
  }

  async setDefaultPaymentMethod(id) {
    return this.request(`/billing/payment-methods/${id}/default`, {
      method: 'PUT',
    });
  }
  
  async deletePaymentMethod(id) {
    return this.request(`/billing/payment-methods/${id}`, {
      method: 'DELETE',
    });
  }
  
  // Workspace Billing
  async assignBillingProfile(workspaceId, billingProfileId) {
    return this.request(`/billing/workspaces/${workspaceId}/profile`, {
      method: 'PUT',
      body: JSON.stringify({ billing_profile_id: billingProfileId }),
    });
  }
  
  // Invoices
  async getInvoices(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/billing/invoices${query ? `?${query}` : ''}`);
  }
  
  async getInvoiceDownloadUrl(id) {
    return this.request(`/billing/invoices/${id}/download`);
  }
  
  async syncInvoices() {
    return this.request('/billing/invoices/sync', {
      method: 'POST',
    });
  }
  
  // Checkout & Portal
  async createCheckout(billingProfileId, workspaceId, planId) {
    return this.request('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({
        billing_profile_id: billingProfileId,
        workspace_id: workspaceId,
        plan_id: planId,
      }),
    });
  }
  
  async createBillingPortal(billingProfileId) {
    return this.request('/billing/portal', {
      method: 'POST',
      body: JSON.stringify({ billing_profile_id: billingProfileId }),
    });
  }
  
  // Coupons
  async validateCoupon(code, planId = null) {
    return this.request('/billing/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ code, plan_id: planId }),
    });
  }

  // Gateway Configuration
  async getGatewayConfig() {
    return this.request('/billing/gateway-config');
  }

  // ==================== HELPHUB ====================

  async getSystemStatus() {
    return this.request('/help/status');
  }

  async getFrillToken() {
    return this.request('/help/frill-token');
  }

  async getUserTickets() {
    return this.request('/help/tickets');
  }

  async getUnreadChangelogCount() {
    try {
      const result = await this.request('/help/changelog/unread');
      return result.count || 0;
    } catch (e) {
      return 0;
    }
  }
}

const apiService = new ApiService();

export default apiService;
